import { useMemo } from 'react'
import PropTypes from 'prop-types'

import { batchElementId } from '@aytm/app/stats/page/components/lib/utils'

import ItemsList from '@aytm/app/stats/page/question-views/components/items-list/ItemsList'
import { ItemBody, ItemHeader, isCollapsable } from '@aytm/app/stats/page/question-views/mx/components/items/Item'
import { calcOpenKeys } from '@aytm/app/stats/page/question-views/mx/lib/open-batches'

// The batch list of a matrix-family question: one collapsible row per batch, each expanding to that
// batch's own block. Replaces subitems/matrix.coffee and the sub-item view under it.
//
// Controlled, like every other sub-items list: which batches are open lives with the shell, which
// owns a set of report params per batch and supplies its resolved block props.

const Items = ({ questionId, batches, questionType, components, uncollapsed, batchPropsOf, onToggle }) => {
  const items = useMemo(() => (batches || []).filter(Boolean), [batches])

  const openKeys = useMemo(() => calcOpenKeys({ batches: items, uncollapsed }), [items, uncollapsed])

  // a question whose items do not open is drawn open, with no row to click
  const expandable = isCollapsable(components)

  return (
    <ItemsList
      items={items}
      itemId={batch => batchElementId(questionId, batch.position)}
      itemKey={batch => batch.position}
      openKeys={expandable ? openKeys : items.map(batch => String(batch.position))}
      renderHeader={(batch, { collapsed }) => (
        <ItemHeader item={batch} questionType={questionType} collapsed={collapsed} expandable={expandable} />
      )}
      renderBody={batch => {
        const batchProps = batchPropsOf?.(batch)
        if (!batchProps) return null

        // A row clicked anywhere inside a batch filters by that batch's sub-question. The surfaces
        // report a click as (position, variants) -- a sentiments or tags row names its own variant
        // there, and only that list knows the tag -- so this reshapes it into what the shell takes.
        const handlers = {
          ...batchProps.handlers,
          onLabelClick: (event, position, answerVariants) =>
            batchProps.handlers?.onLabelClick?.(event, position, batch.position, answerVariants)
        }

        return <ItemBody {...batchProps} handlers={handlers} />
      }}
      onToggle={(key, collapsed) => (expandable ? onToggle?.(key, collapsed) : undefined)}
    />
  )
}

Items.propTypes = {
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  batches: PropTypes.array,
  questionType: PropTypes.string,
  components: PropTypes.object,
  uncollapsed: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  // (batch) => props for that batch's block, or null where the shell has built none
  batchPropsOf: PropTypes.func,
  onToggle: PropTypes.func
}

Items.displayName = 'MatrixBatches'

export default Items
